/*
Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/
import { useEffect } from "react";
import {
  type Participant,
  type Room,
  RoomEvent,
  Track,
  VideoQuality,
} from "livekit-client";
import { monitorRoomHealth, participantHealth } from "./participantHealth";

/** Limit only our camera uplink. The shared health store owns the recovery delay. */
export function useAdaptiveVideoQuality(room?: Room): void {
  useEffect(() => {
    if (!room) return;
    const stopMonitoring = monitorRoomHealth(room);
    const health = participantHealth(room.localParticipant);
    let limited = false;
    const apply = (): void =>
      room.localParticipant
        .getTrackPublication(Track.Source.Camera)
        ?.videoTrack?.setPublishingQuality(
          limited ? VideoQuality.LOW : VideoQuality.HIGH,
        );
    const update = (): void => {
      const poor = ["poor", "lost", "uplink"].includes(health.snapshot() ?? "");
      if (poor !== limited) {
        limited = poor;
        apply();
      }
    };
    const serverUpdate = (
      _quality?: unknown,
      participant?: Participant,
    ): void => {
      if (!participant || participant === room.localParticipant)
        health.update();
    };
    const unsubscribe = health.subscribe(update);
    update();
    room.on(RoomEvent.ConnectionQualityChanged, serverUpdate);
    const published = (): void => {
      if (limited) apply();
    };
    room.on(RoomEvent.LocalTrackPublished, published);
    return () => {
      unsubscribe();
      stopMonitoring();
      room.off(RoomEvent.ConnectionQualityChanged, serverUpdate);
      room.off(RoomEvent.LocalTrackPublished, published);
      if (limited) {
        limited = false;
        apply();
      }
    };
  }, [room]);
}
